import type { ReactNode } from "react";
import { ChevronsUpDown } from "lucide-react";
import { SearchableCombobox, cn } from "@nexus/ui";
import {
  ORG_PICKER_ITEM_CLASS,
  ORG_PICKER_POPOVER_CLASS,
  OrgPickerOptionContent,
  OrgTypeBadge,
  orgPickerItemStyle,
} from "./org-picker-shared";
import { OrgLogoMark } from "./OrgLogoMark";
import { tenantAccentColor } from "@/features/identity-hub/utils/iam.utils";

export interface OrgPickerItem {
  id: string;
  name: string;
  type?: string;
  logoUrl?: string | null;
  brandColor?: string | null;
}

interface OrgPickerComboboxProps {
  items: OrgPickerItem[];
  value: string;
  onValueChange: (id: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  /** Accessible name for the trigger (e.g. links to a visible label). */
  ariaLabel?: string;
  /** Keep the popover open after a selection — useful for picking several in a row. */
  closeOnSelect?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Optional content pinned below the list (e.g. a "Select all" action). */
  footer?: ReactNode;
  /** Extra classes for the popover surface (e.g. to cap its width). */
  contentClassName?: string;
}

function OrgPickerTriggerContent({
  org,
  placeholder,
}: {
  org: OrgPickerItem | null;
  placeholder: string;
}) {
  if (!org) {
    return (
      <span className="min-w-0 flex-1 truncate text-muted-foreground">
        {placeholder}
      </span>
    );
  }

  const accent = tenantAccentColor(org);

  return (
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <OrgLogoMark
        name={org.name}
        logoUrl={org.logoUrl}
        accent={accent}
        isSuperTenant={org.type === "SUPER_TENANT"}
        size={20}
      />
      <span className="min-w-0 truncate text-foreground">{org.name}</span>
      <OrgTypeBadge type={org.type} />
    </div>
  );
}

export function OrgPickerCombobox({
  items,
  value,
  onValueChange,
  placeholder = "Select an organization",
  searchPlaceholder = "Search organizations…",
  emptyMessage = "No organizations found.",
  disabled = false,
  className,
  ariaLabel,
  closeOnSelect = true,
  open,
  onOpenChange,
  footer,
  contentClassName,
}: OrgPickerComboboxProps) {
  return (
    <SearchableCombobox
      items={items}
      getKey={(org) => org.id}
      getLabel={(org) => org.name}
      value={value || null}
      onSelect={(org) => onValueChange(org.id)}
      disabled={disabled}
      closeOnSelect={closeOnSelect}
      open={open}
      onOpenChange={onOpenChange}
      footer={footer}
      searchPlaceholder={searchPlaceholder}
      emptyMessage={emptyMessage}
      contentClassName={cn(
        ORG_PICKER_POPOVER_CLASS,
        "[&_[data-slot=command]]:bg-card [&_[data-slot=command-input-wrapper]]:border-border/70",
        contentClassName,
      )}
      itemClassName={ORG_PICKER_ITEM_CLASS}
      getItemStyle={orgPickerItemStyle}
      renderTrigger={({ selected, open }) => (
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn(
            "flex h-9 w-full items-center gap-2 rounded-md border border-border bg-background px-3 text-sm outline-none",
            "focus:border-ring focus:ring-1 focus:ring-ring/40",
            open && "border-ring ring-1 ring-ring/40",
            className,
          )}
        >
          <OrgPickerTriggerContent org={selected} placeholder={placeholder} />
          <ChevronsUpDown
            className="size-3.5 shrink-0 opacity-50"
            aria-hidden
          />
        </button>
      )}
      renderItem={(org) => <OrgPickerOptionContent org={org} />}
    />
  );
}
